import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Marker, type MapRef } from '@vis.gl/react-maplibre';
import type { Map as MapLibreMap, GeoJSONSource } from 'maplibre-gl';
import MapView from 'components/Map/MapView';
import { decodeGeohash, decodeGeohashBounds } from 'utils/geohash';
import { type LoadingPoint, type DumpingPoint } from 'store/site-config.slice';
import LocationImporterAPI, {
  type ImportedLocation,
} from 'api/location-importer.api';
import useCustomerInfo from 'hooks/useCustomerInfo';
import { hasNoSatelliteCoverage } from 'utils/siteRoadOverlay';

interface DispatchPlanMapProps {
  loadingPoints: LoadingPoint[];
  dumpingPoints: DumpingPoint[];
  activeLoadingIds: string[];
  activeDumpingIds: string[];
  enableDarkTheme: boolean;
}

interface SiteZoneData {
  id: string;
  name: string;
  type: 'loading' | 'dumping';
  geohashes: string[];
  centroidLat: number;
  centroidLon: number;
}

const LOADING_COLOR = '#4ade80';
const DUMPING_COLOR = '#fbbf24';

// Default fallback camera location/zoom when no zones are available — replaces
// the old Cartesian3.fromDegrees(77.059324, 28.392121, 3000) Cesium camera.
const DEFAULT_CENTER = { lon: 77.059324, lat: 28.392121 };
const DEFAULT_ZOOM = 14;
// Zoom used when centering on the average of the active zones — replaces the
// old Cartesian3.fromDegrees(avgLon, avgLat, 2000) Cesium camera altitude.
const ZONES_AVG_ZOOM = 15;

const ZONES_SOURCE_ID = 'dispatch-plan-zones';
const ZONES_FILL_LAYER_ID = 'dispatch-plan-zones-fill';
const ZONES_LINE_LAYER_ID = 'dispatch-plan-zones-line';

const buildZonesGeoJson = (
  zones: SiteZoneData[]
): GeoJSON.FeatureCollection => {
  const features: GeoJSON.Feature[] = [];

  zones.forEach((zone) => {
    zone.geohashes.forEach((gh, idx) => {
      try {
        const { minLat, maxLat, minLon, maxLon } = decodeGeohashBounds(gh);
        features.push({
          type: 'Feature',
          properties: { zoneType: zone.type, zoneId: zone.id },
          geometry: {
            type: 'Polygon',
            coordinates: [
              [
                [minLon, minLat],
                [maxLon, minLat],
                [maxLon, maxLat],
                [minLon, maxLat],
                [minLon, minLat],
              ],
            ],
          },
        });
      } catch {
        // Skip cells whose geohash fails to decode into bounds
      }
    });
  });

  return { type: 'FeatureCollection', features };
};

const removeZonesLayer = (map: MapLibreMap) => {
  if (map.getLayer(ZONES_LINE_LAYER_ID)) map.removeLayer(ZONES_LINE_LAYER_ID);
  if (map.getLayer(ZONES_FILL_LAYER_ID)) map.removeLayer(ZONES_FILL_LAYER_ID);
  if (map.getSource(ZONES_SOURCE_ID)) map.removeSource(ZONES_SOURCE_ID);
};

const addZonesLayer = (
  map: MapLibreMap,
  geojson: GeoJSON.FeatureCollection
) => {
  map.addSource(ZONES_SOURCE_ID, { type: 'geojson', data: geojson });
  map.addLayer({
    id: ZONES_FILL_LAYER_ID,
    type: 'fill',
    source: ZONES_SOURCE_ID,
    paint: {
      'fill-color': [
        'match',
        ['get', 'zoneType'],
        'loading',
        LOADING_COLOR,
        'dumping',
        DUMPING_COLOR,
        '#94a3b8',
      ],
      'fill-opacity': 0.55,
    },
  });
  map.addLayer({
    id: ZONES_LINE_LAYER_ID,
    type: 'line',
    source: ZONES_SOURCE_ID,
    paint: {
      'line-color': [
        'match',
        ['get', 'zoneType'],
        'loading',
        LOADING_COLOR,
        'dumping',
        DUMPING_COLOR,
        '#94a3b8',
      ],
      'line-width': 2,
    },
  });
};

const DispatchPlanMap: React.FC<DispatchPlanMapProps> = ({
  loadingPoints,
  dumpingPoints,
  activeLoadingIds,
  activeDumpingIds,
  enableDarkTheme,
}) => {
  const { t } = useTranslation();
  const { customerCode } = useCustomerInfo();
  const mapRef = useRef<MapRef | null>(null);
  const locationImporterApi = useRef(new LocationImporterAPI()).current;
  const [importedLocations, setImportedLocations] = useState<
    ImportedLocation[]
  >([]);

  useEffect(() => {
    locationImporterApi
      .getLocations()
      .then((locs: ImportedLocation[]) => {
        setImportedLocations(locs);
      })
      .catch(() => {});
  }, []);

  const zones = React.useMemo<SiteZoneData[]>(() => {
    const result: SiteZoneData[] = [];

    activeLoadingIds.forEach((id) => {
      const lp = loadingPoints.find((p) => p.id === id);
      if (!lp) return;

      const imported = importedLocations.find(
        (loc) => loc.name === lp.name && loc.locationType === 'loading'
      );

      const geohashes = imported?.geohashes?.length
        ? imported.geohashes
        : lp.location
        ? [lp.location]
        : [];

      if (geohashes.length === 0) return;

      let centroidLat: number;
      let centroidLon: number;
      if (imported?.centroidLat != null && imported?.centroidLon != null) {
        centroidLat = imported.centroidLat;
        centroidLon = imported.centroidLon;
      } else {
        const decoded = decodeGeohash(geohashes[0]);
        centroidLat = decoded.lat;
        centroidLon = decoded.lon;
      }

      result.push({
        id: lp.id,
        name: lp.name || lp.id,
        type: 'loading',
        geohashes,
        centroidLat,
        centroidLon,
      });
    });

    activeDumpingIds.forEach((id) => {
      const dp = dumpingPoints.find((p) => p.id === id);
      if (!dp) return;

      const imported = importedLocations.find(
        (loc) => loc.name === dp.name && loc.locationType === 'dumping'
      );

      const geohashes = imported?.geohashes?.length
        ? imported.geohashes
        : dp.location
        ? [dp.location]
        : [];

      if (geohashes.length === 0) return;

      let centroidLat: number;
      let centroidLon: number;
      if (imported?.centroidLat != null && imported?.centroidLon != null) {
        centroidLat = imported.centroidLat;
        centroidLon = imported.centroidLon;
      } else {
        const decoded = decodeGeohash(geohashes[0]);
        centroidLat = decoded.lat;
        centroidLon = decoded.lon;
      }

      result.push({
        id: dp.id,
        name: dp.name || dp.id,
        type: 'dumping',
        geohashes,
        centroidLat,
        centroidLon,
      });
    });

    return result;
  }, [
    loadingPoints,
    dumpingPoints,
    activeLoadingIds,
    activeDumpingIds,
    importedLocations,
  ]);

  const zonesGeoJson = useMemo(() => buildZonesGeoJson(zones), [zones]);

  // Add/update the zone-cell fill+outline layer whenever the zone set changes.
  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const applyZones = (map: MapLibreMap) => {
      if (cancelled) return;
      const source = map.getSource<GeoJSONSource>(ZONES_SOURCE_ID);
      if (source) {
        void source.setData(zonesGeoJson);
        return;
      }
      removeZonesLayer(map);
      addZonesLayer(map, zonesGeoJson);
    };

    const applyWhenReady = () => {
      if (cancelled) return;
      const map = mapRef.current?.getMap();
      if (!map?.isStyleLoaded()) {
        timer = setTimeout(applyWhenReady, 150);
        return;
      }
      applyZones(map);
    };

    applyWhenReady();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [zonesGeoJson]);

  // Fly the camera to the average of the active zones, or a fallback point —
  // replaces the old viewer.camera.setView(Cartesian3.fromDegrees(...)) snap.
  useEffect(() => {
    const timer = setTimeout(() => {
      const map = mapRef.current?.getMap();
      if (!map) return;

      if (zones.length > 0) {
        const avgLat =
          zones.reduce((s, z) => s + z.centroidLat, 0) / zones.length;
        const avgLon =
          zones.reduce((s, z) => s + z.centroidLon, 0) / zones.length;
        map.jumpTo({ center: [avgLon, avgLat], zoom: ZONES_AVG_ZOOM });
      } else {
        map.jumpTo({
          center: [DEFAULT_CENTER.lon, DEFAULT_CENTER.lat],
          zoom: DEFAULT_ZOOM,
        });
      }
    }, 150);
    return () => {
      clearTimeout(timer);
    };
  }, [zones]);

  const bgColor = enableDarkTheme ? '#0f172a' : '#f1f5f9';
  const borderColor = enableDarkTheme ? '#1e293b' : '#e2e8f0';
  const textMuted = enableDarkTheme ? '#94a3b8' : '#64748b';
  const textPrimary = enableDarkTheme ? '#e2e8f0' : '#1a202c';

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        bgcolor: bgColor,
        border: `1px solid ${borderColor}`,
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 2,
          py: 1.25,
          borderBottom: `1px solid ${borderColor}`,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: textPrimary }}>
          {t('dispatch_plan_map_title')}
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: 1,
                bgcolor: LOADING_COLOR,
              }}
            />
            <Typography sx={{ fontSize: 11, color: textMuted }}>
              {t('dispatch_plan_map_loading')}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: 1,
                bgcolor: DUMPING_COLOR,
              }}
            />
            <Typography sx={{ fontSize: 11, color: textMuted }}>
              {t('dispatch_plan_map_dumping')}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Map */}
      <Box sx={{ flex: 1, position: 'relative' }}>
        {zones.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              color: textMuted,
              fontSize: 13,
            }}
          >
            {t('dispatch_plan_map_no_locations')}
          </Box>
        ) : (
          <MapView
            ref={mapRef}
            basemap={
              hasNoSatelliteCoverage(customerCode) ? undefined : 'satellite'
            }
            style={{ width: '100%', height: '100%' }}
            initialViewState={{
              longitude: DEFAULT_CENTER.lon,
              latitude: DEFAULT_CENTER.lat,
              zoom: DEFAULT_ZOOM,
            }}
          >
            {zones.map((zone) => (
              <Marker
                key={`label-${zone.id}`}
                longitude={zone.centroidLon}
                latitude={zone.centroidLat}
              >
                <Typography
                  sx={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: '#ffffff',
                    textShadow:
                      '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000',
                    whiteSpace: 'pre-line',
                    textAlign: 'center',
                    pointerEvents: 'none',
                  }}
                >
                  {`${zone.id}\n${zone.name}`}
                </Typography>
              </Marker>
            ))}
          </MapView>
        )}
      </Box>
    </Box>
  );
};

export default DispatchPlanMap;
